import type { GridSelection } from "@glideapps/glide-data-grid";
import { useEffect } from "react";
import type {
  AiScriptChatSelectionContext,
  IAiScriptEditedCellSelection,
  IAiSpreadsheetLoadedSheet,
} from "@/payroll/ai-script/types";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import { buildAiScriptChatSelectionContext } from "./chat-selection-context";
import { getSavedSpreadsheetRow } from "./spreadsheet-region-view";
import { getEditedCellSelection } from "./utils";

interface UseSelectionReportingParams {
  gridSelection: GridSelection;
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  activeLoadedSheet: IAiSpreadsheetLoadedSheet | null;
  regionRowMap: number[];
  onSelectionContextChange:
    | ((context: AiScriptChatSelectionContext | null) => void)
    | undefined;
  onEditedCellSelectionChange:
    | ((selection: IAiScriptEditedCellSelection | null) => void)
    | undefined;
}

export const useSelectionReporting = ({
  gridSelection,
  adaptedSheet,
  activeLoadedSheet,
  regionRowMap,
  onSelectionContextChange,
  onEditedCellSelectionChange,
}: UseSelectionReportingParams) => {
  useEffect(() => {
    if (!onSelectionContextChange) return;

    if (!adaptedSheet || !activeLoadedSheet) {
      onSelectionContextChange(null);
      return;
    }

    onSelectionContextChange(
      buildAiScriptChatSelectionContext({
        adaptedSheet,
        activeSheet: activeLoadedSheet,
        gridSelection,
        getSavedRow: (displayRow) =>
          getSavedSpreadsheetRow(regionRowMap, displayRow),
      }),
    );
  }, [
    gridSelection,
    adaptedSheet,
    activeLoadedSheet,
    regionRowMap,
    onSelectionContextChange,
  ]);

  useEffect(() => {
    if (!onEditedCellSelectionChange || !adaptedSheet) return;

    const cell = gridSelection.current?.cell;
    const record = cell
      ? adaptedSheet.getCellRecord(cell[0], cell[1])
      : undefined;

    onEditedCellSelectionChange(getEditedCellSelection(record));
  }, [gridSelection, adaptedSheet, onEditedCellSelectionChange]);
};
